import { ToolSwapAction } from './ToolSwapAction';
/**
 * Kalah 盤 (6 ピット + 2 ストア) コンポーネント。 SVG で横長の木目盤を描き、
 * 中央 2 行 × 6 列のピットと両端の大型ストアを並べる。
 *
 * 視点 (= 自分が常に下側):
 *   - yourSide==='Second' のときは盤を上下反転して、 自分 (Second) の列を下段に置く。
 *     ストアの左右も入れ替える (= 自分のストア常に右端)。
 *   - yourSide が null / 'First' のときは標準レイアウト (First 下、 S1 右)。
 *
 * 種石は count 個ぴったり描画 (4+chip 表示はやめ)。 pit/store のセル内に grid 配置で
 * 入りきるよう半径を自動縮小する。
 *
 * lastMove ハイライト: 直前に撒いた元 pit を強調する。 sow 元は撒いた直後に空 (seeds==0)
 * になるため、 以前は「seeds>0 のときだけ太枠」 としていたが、 それだと sow 元は常に空なので
 * ハイライトが一度も出なかった。 legal リング (黄) との混同を避けるため、 最終手枠は別色 (水色)
 * + seeds の有無に依らず常に表示する。
 */

import {
  isSow,
  type KalahMove,
  type KalahState,
  ownPitIndices,
  ownStoreIndex,
  type Side,
  STORE_FIRST,
  STORE_SECOND,
} from '../../games/kalah';

interface Props {
  state: KalahState;
  legal?: KalahMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: KalahMove) => void;
  mini?: boolean;
  lastMove?: KalahMove;
  staticView?: boolean;
  /** チュートリアル: 強調する pit index (0..13) の配列。 */
  tutorialHighlight?: number[];
}

// SVG 座標系: viewBox 480 x 200。 board frame 12:5 aspect.
// 中央エリア: pit 6 列、 各 60 幅 + gap。 端のストア領域は 60 幅。
const VB_W = 480;
const VB_H = 200;
const STORE_W = 60;
const STORE_H = 160;
const PIT_W = 50;
const PIT_H = 56;
const PIT_GAP = 8;
const ROW_GAP = 20;

interface PitLayout {
  idx: number;
  cx: number;
  cy: number;
  w: number;
  h: number;
  isStore: boolean;
  ownerLabel: string;
}

/** 各 14 セル (P1..S2) の SVG 座標。 flip=true なら First/Second を上下反転 + 左右逆。 */
function computeLayout(flip: boolean): PitLayout[] {
  const layout: PitLayout[] = [];
  const colCenters: number[] = [];
  const pitsStartX = STORE_W + 14;
  for (let c = 0; c < 6; c++) {
    colCenters.push(pitsStartX + PIT_W / 2 + c * (PIT_W + PIT_GAP));
  }
  const topY = 40;
  const botY = topY + PIT_H + ROW_GAP;
  // flip=false: First 下、 Second 上、 S1 右、 S2 左
  // flip=true:  Second 下、 First 上、 S2 右、 S1 左
  const firstY = flip ? topY : botY;
  const secondY = flip ? botY : topY;

  // Second の P7..P12 (idx 7..12): 反時計回りに合わせて
  //   flip=false (= 上段 Second): SVG col 0 (左端) に P12、 col 5 (右端) に P7
  //   flip=true  (= 下段 Second): SVG col 0 (左端) に P7、  col 5 (右端) に P12
  for (let c = 0; c < 6; c++) {
    const idx = flip ? 7 + c : 12 - c;
    layout.push({
      idx,
      cx: colCenters[c],
      cy: secondY,
      w: PIT_W,
      h: PIT_H,
      isStore: false,
      ownerLabel: 'Second',
    });
  }

  // First の P1..P6 (idx 0..5):
  //   flip=false (= 下段 First): SVG col 0 (左端) に P1、 col 5 (右端) に P6 (左→右)
  //   flip=true  (= 上段 First): SVG col 0 (左端) に P6、 col 5 (右端) に P1 (右→左)
  for (let c = 0; c < 6; c++) {
    const idx = flip ? 5 - c : c;
    layout.push({
      idx,
      cx: colCenters[c],
      cy: firstY,
      w: PIT_W,
      h: PIT_H,
      isStore: false,
      ownerLabel: 'First',
    });
  }

  const cyMid = (topY + botY) / 2;
  // flip=false: Second store (S2) 左、 First store (S1) 右
  // flip=true : First store (S1) 左、 Second store (S2) 右
  layout.push({
    idx: flip ? STORE_FIRST : STORE_SECOND,
    cx: STORE_W / 2 + 4,
    cy: cyMid,
    w: STORE_W,
    h: STORE_H,
    isStore: true,
    ownerLabel: flip ? 'First' : 'Second',
  });
  layout.push({
    idx: flip ? STORE_SECOND : STORE_FIRST,
    cx: VB_W - STORE_W / 2 - 4,
    cy: cyMid,
    w: STORE_W,
    h: STORE_H,
    isStore: true,
    ownerLabel: flip ? 'Second' : 'First',
  });

  return layout;
}

/** (pitIdx, seedIdx) を seed として deterministic に 0..1 の擬似乱数を返す。
 *  ハッシュは xorshift 風。 同じ (pit, idx) なら毎回同じ値 → 描画が安定。 */
function hash01(pit: number, idx: number, salt: number): number {
  let x = (pit * 374761393) ^ (idx * 668265263) ^ (salt * 2147483647);
  x = (x ^ (x >>> 13)) >>> 0;
  x = Math.imul(x, 1274126177) >>> 0;
  x = (x ^ (x >>> 16)) >>> 0;
  return x / 4294967296;
}

/** pit 内に種を `count` 個、 固定サイズで ハッシュベースの安定ランダム配置で描画。
 *  - サイズは count に依存しない (= 視認性を保つ)
 *  - (pitIdx, seedIdx) のハッシュから (x, y) を決定 → 同じ count なら毎回同じ絵
 *  - 多すぎる場合は重なるが、 count 自体は別途数字で表示しているので OK */
function renderSeeds(
  pitIdx: number,
  count: number,
  cx: number,
  cy: number,
  w: number,
  h: number,
  isStore: boolean,
): JSX.Element[] {
  if (count === 0) return [];
  // 固定半径 (旧仕様)
  const r = isStore ? 5 : 4.5;
  // セル内に置ける中心 (x, y) 範囲。 石が枠に被らないよう半径 + 少し余白で内側に。
  const padX = (isStore ? 6 : 4) + r;
  const padY = (isStore ? 8 : 4) + r;
  const innerW = Math.max(2, w - padX * 2);
  const innerH = Math.max(2, h - padY * 2);
  const out: JSX.Element[] = [];
  for (let i = 0; i < count; i++) {
    const u = hash01(pitIdx, i, 1);
    const v = hash01(pitIdx, i, 2);
    const x = cx + (u - 0.5) * innerW;
    const y = cy + (v - 0.5) * innerH;
    out.push(
      <circle
        key={`seed-${i}`}
        cx={x}
        cy={y}
        r={r}
        className="kalah-seed"
        fill="#d4a455"
        stroke="#5a3917"
        strokeWidth={0.8}
      />,
    );
  }
  return out;
}

export function KalahBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  tutorialHighlight,
}: Props) {
  if (!state || !Array.isArray(state.board) || state.board.length !== 14) {
    return <div className="kalah-frame" />;
  }
  const isMyTurn =
    myTurnAllowed !== undefined
      ? myTurnAllowed
      : yourSide != null && yourSide === state.to_move;

  // 合法 sow 元 pit の集合
  const legalPits = new Set<number>();
  let canSwap = false;
  for (const m of legal ?? []) {
    if (isSow(m)) {
      legalPits.add(m.Sow.pit);
    } else if (m === 'Swap') {
      canSwap = true;
    }
  }

  // last move の発火 pit (= 撒いた元 pit)。 撒いた直後は空になるが、 別色枠なので legal
  // リングとは混同しない。
  const lastPit = lastMove && isSow(lastMove) ? lastMove.Sow.pit : null;

  // チュートリアル強調 pit (= オレンジ太枠)。 legal リング (黄) / last (水色) と別色。
  const highlightSet = new Set(tutorialHighlight ?? []);

  // 自分が常に下側になるよう、 yourSide==='Second' のとき盤を反転する。
  // yourSide==null (観戦中等) は flip しない。
  const flip = yourSide === 'Second';
  const layout = computeLayout(flip);

  const ownStoreFor = (side: Side | null | undefined) =>
    side ? ownStoreIndex(side) : null;
  const myStore = ownStoreFor(yourSide);
  const oppStore = yourSide
    ? ownStoreIndex(yourSide === 'First' ? 'Second' : 'First')
    : null;

  // own pit の集合 (色分け用)
  const myOwn = new Set(yourSide ? ownPitIndices(yourSide) : []);

  // 自分の番 + free_turn_streak >= 1 のときに 「FREE TURN」 ラベル
  const showFreeTurn =
    !mini &&
    !staticView &&
    isMyTurn &&
    state.free_turn_streak >= 1 &&
    state.outcome == null;

  const showOppHint = !mini && !staticView && !isMyTurn && state.outcome == null;

  // ストアスコア。 mini では大きめ。
  const s1 = state.board[STORE_FIRST];
  const s2 = state.board[STORE_SECOND];

  return (
    <div className={`kalah-board ${mini ? 'mini' : ''}`}>
      {!mini && (
        <div className="kalah-score" data-testid="kalah-score">
          <span className={`kalah-score-side ${state.to_move === 'First' ? 'turn' : ''}`}>
            <span className="kalah-score-label">先</span>
            <span className="kalah-score-val">{s1}</span>
          </span>
          <span className="kalah-score-mid">
            {showFreeTurn && (
              <span className="kalah-free-turn" data-testid="kalah-free-turn">
                FREE TURN!
                {state.free_turn_streak >= 2 ? ` ×${state.free_turn_streak}` : ''}
              </span>
            )}
          </span>
          <span className={`kalah-score-side ${state.to_move === 'Second' ? 'turn' : ''}`}>
            <span className="kalah-score-label">後</span>
            <span className="kalah-score-val">{s2}</span>
          </span>
        </div>
      )}
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="kalah-svg"
        role="img"
        aria-label="Kalah board"
      >
        {/* 盤の木目 (ベース) */}
        <rect
          x={2}
          y={2}
          width={VB_W - 4}
          height={VB_H - 4}
          rx={14}
          fill="#7a4a1d"
          stroke="#3a2208"
          strokeWidth={2}
        />
        {/* 中央エリア 内堀 */}
        <rect
          x={STORE_W + 6}
          y={20}
          width={VB_W - 2 * (STORE_W + 6)}
          height={VB_H - 40}
          rx={8}
          fill="#5a3614"
          opacity={0.55}
        />

        {layout.map((p) => {
          const seeds = state.board[p.idx];
          const isLegal = isMyTurn && legalPits.has(p.idx);
          // 最終手の sow 元 pit を強調 (空でも表示)。 legal リング (黄) と区別するため水色。
          const isLast = !mini && lastPit === p.idx;
          const isHighlighted = highlightSet.has(p.idx);
          const isMine = myOwn.has(p.idx);
          const isMyStore = myStore === p.idx;
          const isOppStore = oppStore === p.idx;

          const x = p.cx - p.w / 2;
          const y = p.cy - p.h / 2;

          // ストア = 縦長楕円風、 ピット = 円形。
          const pitFill = isMyStore || isOppStore ? '#3a2208' : '#3e2410';
          const stroke = isHighlighted ? '#ff9d3c' : isLast ? '#5cc8ef' : '#1c0e02';
          const strokeW = isHighlighted ? 3.5 : isLast ? 3 : 1.2;

          return (
            <g key={p.idx} className={isLegal ? 'kalah-cell legal' : 'kalah-cell'}>
              {p.isStore ? (
                <rect
                  x={x}
                  y={y}
                  width={p.w}
                  height={p.h}
                  rx={20}
                  ry={28}
                  fill={pitFill}
                  stroke={stroke}
                  strokeWidth={strokeW}
                />
              ) : (
                <ellipse
                  cx={p.cx}
                  cy={p.cy}
                  rx={p.w / 2}
                  ry={p.h / 2}
                  fill={pitFill}
                  stroke={stroke}
                  strokeWidth={strokeW}
                />
              )}
              {isLegal && (
                <ellipse
                  cx={p.cx}
                  cy={p.cy}
                  rx={p.w / 2 + 2}
                  ry={p.h / 2 + 2}
                  fill="none"
                  stroke="#f3c34a"
                  strokeWidth={2}
                  opacity={0.85}
                />
              )}
              {renderSeeds(p.idx, seeds, p.cx, p.cy, p.w, p.h, p.isStore)}
              {/* 種数ラベル (ピットのみ、 ストアは別に大きく表示) */}
              {!p.isStore && (
                <text
                  x={p.cx}
                  y={p.cy + p.h / 2 + 12}
                  fontSize={11}
                  textAnchor="middle"
                  fill="#fafafa"
                  fontWeight={600}
                >
                  {seeds}
                </text>
              )}
              {p.isStore && (
                <text
                  x={p.cx}
                  y={p.cy + 5}
                  fontSize={26}
                  textAnchor="middle"
                  fill="#fafafa"
                  fontWeight={700}
                  className={`kalah-store-label ${isMine ? 'mine' : ''}`}
                >
                  {seeds}
                </text>
              )}

              {/* クリック領域 (legal な own pit のみ) */}
              {isLegal && (
                <rect
                  x={x}
                  y={y}
                  width={p.w}
                  height={p.h}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onMove?.({ Sow: { pit: p.idx } })}
                  data-testid={`kalah-pit-${p.idx}`}
                />
              )}
            </g>
          );
        })}
      </svg>
      {!mini && !staticView && yourSide != null && isMyTurn && state.outcome == null && onMove != null && canSwap &&
        <div className="tool-swap-row"><ToolSwapAction tool="store" label="先手側を引き継ぐ"
          testId="kalah-swap" onClick={() => onMove?.('Swap')} /></div>}
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}
